/**
 * Wire Decomposition Utility
 *
 * Decomposes the wire_from_warehouse into three accounting buckets:
 *   Revenue   — money Lendwise earned
 *   Expense   — money deducted or paid out
 *   Pass-Through — money moving between accounts ($0 P&L impact)
 *
 * Two identities MUST hold:
 *   1. wire = revenueInWire − expenseInWire + passThroughNet
 *   2. netProfit = (revenueInWire + revenueOutside) − (expenseInWire + expenseOutside)
 *
 * Consumers: CompanyRevenueReport, Excel export
 * Source of truth for all financial values: computeBankedRecon() output
 */

import type { BankedReconResult, CostItem } from './bankedNetProfit';
import { createComponentLogger } from '@/utils/logger';
import { isBuydownPassThrough } from './buydownPassThrough';
import { isManualAdjustmentLineType } from './manualAdjustments';
import {
  isGovernmentFundingPassThrough,
  isRetainedClosingFeeLineItem,
  resolveWarehouseCashBasis,
  type RetainedClosingFeeBasis,
  type WarehouseBasisMode,
  type WarehouseBasisModeUsed
} from './wireReconciliation';

const log = createComponentLogger('WireDecomposition', 'banker-recon');

// ============================================================================
// CLASSIFICATION CONSTANTS
// ============================================================================

/** Warehouse cost line types that are always pass-through regardless of sign */
const PASS_THROUGH_LINE_TYPES = new Set(['shortage_funded']);

/** Defensive: catches shortage_recouped if it appears in loan_revenue items */
const SHORTAGE_PREFIX = 'shortage';

/** Investor cost line types that are escrow pass-through (cancel with escrow in fees) */
const ESCROW_PREFIX = 'escrow_deposit';

function toLoanRevenueLineItem(item: CostItem) {
  return {
    category: 'loan_revenue',
    line_type: item.lineType,
    description: item.description,
    amount: item.amount,
    nets_to_warehouse_advance: item.netsToWarehouseAdvance ?? null,
    warehouse_retained_amount: item.warehouseRetainedAmount ?? null
  };
}

// ============================================================================
// TYPES
// ============================================================================

export type DecompositionBucket =
  | 'revenue_in_wire'
  | 'expense_in_wire'
  | 'pass_through_in_wire'
  | 'pass_through_outside'
  | 'expense_outside'
  | 'revenue_outside';

export type ClearingType =
  | 'shortage_funded'
  | 'shortage_recouped'
  | 'escrow'
  | 'servicing_principal'
  | 'non_qm_haircut'
  | 'government_funding'
  | 'fha_ufmip_premium'
  | 'fha_ufmip_hud_remittance'
  | 'buydown_pass_through'
  | 'reimbursable'
  | 'retained_at_closing'
  | 'manual_pass_through'
  | 'other';

export interface WireDecompositionItem {
  description: string;
  amount: number;            // positive for display
  bucket: DecompositionBucket;
  wireEffect: number;        // signed: positive adds to wire, negative subtracts
  source: 'line_item' | 'computed';
  clearingType?: ClearingType;
}

export interface WireDecomposition {
  items: WireDecompositionItem[];

  // Aggregates
  revenueInWire: number;
  expenseInWire: number;
  passThroughNet: number;    // signed wire effect of all pass-through items
  passThroughOutside: number; // signed clearing cash outside the warehouse wire
  expenseOutside: number;
  revenueOutside: number;

  // Derived totals
  totalRevenue: number;      // revenueInWire + revenueOutside
  totalExpenses: number;     // expenseInWire + expenseOutside

  // Verification
  wireFromWarehouse: number;
  computedWire: number;      // revenueInWire - expenseInWire + passThroughNet
  netProfit: number;         // totalRevenue - totalExpenses
  isWireBalanced: boolean;
  isPnlBalanced: boolean;

  // Warehouse cash basis audit
  basisModeUsed: WarehouseBasisModeUsed;
  cdCreditNettedToWarehouseAdvance: number | null;
  netPayableToLenderAtClosing: number | null;
  cashNeededAtClosing: number | null;
}

/** Clearing activity still exists when its individual amounts net to $0. */
export function hasClearingActivity(decomposition: Pick<WireDecomposition, 'items'>): boolean {
  return decomposition.items.some(item =>
    (item.bucket === 'pass_through_in_wire' || item.bucket === 'pass_through_outside')
    && (Math.abs(item.amount) >= 0.005 || Math.abs(item.wireEffect) >= 0.005)
  );
}

export interface DecomposeWireInput {
  reconResult: BankedReconResult;
  wireFromWarehouse: number;
  loanAmount: number;
  haircutPercent: number;     // default 0
  escrowDeposit: number;      // from loan_snapshot, default 0
  servicingPrincipalCollected?: number; // borrower principal that reduced investor purchase wire
  loanType?: string | null;
  retainedClosingFeeBasis?: RetainedClosingFeeBasis;
  basisMode?: WarehouseBasisMode;
  cdCreditNettedToWarehouseAdvance?: number | null;
}

// ============================================================================
// MAIN DECOMPOSITION
// ============================================================================

export function decomposeWire(input: DecomposeWireInput): WireDecomposition {
  const {
    reconResult: r,
    wireFromWarehouse,
    loanAmount,
    haircutPercent,
    escrowDeposit,
    servicingPrincipalCollected = 0,
    loanType
  } = input;
  const items: WireDecompositionItem[] = [];

  // --- 1. Compute retained basis and lender credit/cure timing ---
  // Parity with wireReconciliation: include an allowlisted retained fee OR a row flagged to
  // net to the warehouse advance; contribution = amount + warehouse_retained_amount, once.
  const entersRetainedBasis = (item: CostItem): boolean =>
    isRetainedClosingFeeLineItem(toLoanRevenueLineItem(item), input.retainedClosingFeeBasis)
    || item.netsToWarehouseAdvance === true;
  const retainedUserFees = [...r.nonReimbursableUserFeeItems, ...r.reimbursableItems]
    .filter(entersRetainedBasis)
    .reduce((sum, item) => sum + item.amount + (item.warehouseRetainedAmount || 0), 0);
  const feesAtClosing =
    r.processingFee + r.underwritingFee + r.interestIncome +
    retainedUserFees + escrowDeposit + r.governmentFundingPassThrough + r.buydownPassThrough
    + (r.fhaUfmip?.warehousePremiumAmount || 0);
  // Borrower-facing credits only; other lender costs (e.g. an MI premium paid to the insurer) are
  // plain company expenses outside the wire and are itemized in step 7.
  const totalBorrowerLenderCredit = r.lenderCredit + r.cureAppliedLenderCredit;
  const lenderCreditNettedToWarehouseAdvance = r.lenderCreditItems
    .filter((item) => item.netsToWarehouseAdvance === true)
    .reduce((sum, item) => sum + item.amount, 0);
  let cashBasis = resolveWarehouseCashBasis({
    basisMode: input.basisMode,
    loanAmount,
    feesAtClosing,
    shortageFunded: r.shortageFunded,
    haircutPercent,
    cdCreditNettedToWarehouseAdvance: input.cdCreditNettedToWarehouseAdvance
  });

  if (
    cashBasis.basisModeUsed === 'cdCashRequired' &&
    (cashBasis.cdCreditNettedToWarehouseAdvance ?? 0) > totalBorrowerLenderCredit + 0.01
  ) {
    cashBasis = resolveWarehouseCashBasis({
      basisMode: 'legacy',
      loanAmount,
      feesAtClosing,
      shortageFunded: r.shortageFunded,
      haircutPercent,
      cdCreditNettedToWarehouseAdvance: null
    });
  }

  const lenderCreditInWire = cashBasis.basisModeUsed === 'cdCashRequired'
    ? (cashBasis.cdCreditNettedToWarehouseAdvance ?? 0)
    : Math.min(lenderCreditNettedToWarehouseAdvance, feesAtClosing);
  const excessLC = Math.max(0, totalBorrowerLenderCredit - lenderCreditInWire);

  // --- 2. Defensive: scan nonReimbursableUserFeeItems for shortage_recouped ---
  let shortageRecoupedInRevenue = 0;
  for (const item of r.nonReimbursableUserFeeItems) {
    if (item.lineType.startsWith(SHORTAGE_PREFIX)) {
      shortageRecoupedInRevenue += item.amount;
      items.push({
        description: `${item.description} (reclassified)`,
        amount: Math.abs(item.amount),
        bucket: 'pass_through_in_wire',
        wireEffect: item.amount,
        source: 'line_item',
        clearingType: item.amount < 0 ? 'shortage_funded' : 'shortage_recouped'
      });
      log.warn('shortage item found in loan_revenue — reclassified as pass-through', {
        lineType: item.lineType, amount: item.amount
      });
    }
  }

  // --- 3. Assert warehouse cost invariant ---
  for (const item of r.warehouseCostItems) {
    if (!PASS_THROUGH_LINE_TYPES.has(item.lineType) && item.amount < 0) {
      log.warn('Negative non-shortage warehouse cost detected', {
        lineType: item.lineType, amount: item.amount
      });
    }
  }

  // --- 4. Revenue in wire ---
  let revenueInWire = 0;

  // SRP premium
  if (r.actualPurchaseDollar !== 0) {
    items.push({ description: 'Investor Purchase Premium', amount: Math.abs(r.actualPurchaseDollar), bucket: 'revenue_in_wire', wireEffect: r.actualPurchaseDollar, source: 'computed' });
    revenueInWire += r.actualPurchaseDollar;
  }

  // Standard fees
  if (r.processingFee !== 0) {
    items.push({ description: 'Processing Fee', amount: Math.abs(r.processingFee), bucket: 'revenue_in_wire', wireEffect: r.processingFee, source: 'computed' });
    revenueInWire += r.processingFee;
  }
  if (r.underwritingFee !== 0) {
    items.push({ description: 'Underwriting Fee', amount: Math.abs(r.underwritingFee), bucket: 'revenue_in_wire', wireEffect: r.underwritingFee, source: 'computed' });
    revenueInWire += r.underwritingFee;
  }
  if (r.interestIncome !== 0) {
    items.push({ description: 'Prepaid Interest', amount: Math.abs(r.interestIncome), bucket: 'revenue_in_wire', wireEffect: r.interestIncome, source: 'computed' });
    revenueInWire += r.interestIncome;
  }

  // Non-reimbursable user fees (excluding any shortage_recouped)
  const adjustedUserFees = r.nonReimbursableUserFees - shortageRecoupedInRevenue;
  for (const item of r.nonReimbursableUserFeeItems) {
    // Retained-only rows have no revenue leg; they clear below as retained_at_closing.
    if (item.lineType.startsWith(SHORTAGE_PREFIX) || item.amount === 0) {
      continue;
    }
    items.push({ description: item.description, amount: Math.abs(item.amount), bucket: 'revenue_in_wire', wireEffect: item.amount, source: 'line_item' });
  }
  revenueInWire += adjustedUserFees;

  // FlexClose credit
  if (r.flexcloseCredit > 0) {
    items.push({ description: 'FlexClose Credit', amount: r.flexcloseCredit, bucket: 'revenue_in_wire', wireEffect: r.flexcloseCredit, source: 'computed' });
    revenueInWire += r.flexcloseCredit;
  }

  // Investor credits (negative investor_cost items, not escrow)
  for (const item of r.investorCostItems) {
    if (item.lineType.startsWith(ESCROW_PREFIX)) {
continue;
}
    if (isGovernmentFundingPassThrough({
      category: 'investor_cost',
      line_type: item.lineType,
      description: item.description
    }, loanType)) {
continue;
}
    if (isBuydownPassThrough({
      category: 'investor_cost',
      line_type: item.lineType,
      description: item.description
    })) {
continue;
}
    if (item.amount < 0) {
      items.push({ description: `${item.description} (Credit)`, amount: Math.abs(item.amount), bucket: 'revenue_in_wire', wireEffect: Math.abs(item.amount), source: 'line_item' });
      revenueInWire += Math.abs(item.amount);
    }
  }

  // --- 5. Expense in wire ---
  let expenseInWire = 0;

  // Warehouse costs (aggregates from reconResult — already exclude shortage)
  if (r.warehouseInterest > 0) {
    items.push({ description: 'Warehouse Interest', amount: r.warehouseInterest, bucket: 'expense_in_wire', wireEffect: -r.warehouseInterest, source: 'computed' });
    expenseInWire += r.warehouseInterest;
  }
  if (r.otherWarehouseCosts !== 0) {
    // Show individual items for detail
    for (const item of r.warehouseCostItems) {
      if (item.lineType === 'warehouse_interest') {
continue;
}
      if (PASS_THROUGH_LINE_TYPES.has(item.lineType)) {
continue;
}
      if (item.amount !== 0) {
        items.push({ description: item.description, amount: Math.abs(item.amount), bucket: 'expense_in_wire', wireEffect: -item.amount, source: 'line_item' });
      }
    }
    expenseInWire += r.otherWarehouseCosts;
  }

  // Investor expenses (positive investor_cost items, not escrow)
  for (const item of r.investorCostItems) {
    if (item.lineType.startsWith(ESCROW_PREFIX)) {
continue;
}
    if (isGovernmentFundingPassThrough({
      category: 'investor_cost',
      line_type: item.lineType,
      description: item.description
    }, loanType)) {
continue;
}
    if (isBuydownPassThrough({
      category: 'investor_cost',
      line_type: item.lineType,
      description: item.description
    })) {
continue;
}
    if (item.amount > 0) {
      items.push({ description: item.description, amount: item.amount, bucket: 'expense_in_wire', wireEffect: -item.amount, source: 'line_item' });
      expenseInWire += item.amount;
    }
  }

  // Lender credit that reduced closing cash owed to the warehouse.
  if (lenderCreditInWire > 0) {
    items.push({ description: 'Lender Credit', amount: lenderCreditInWire, bucket: 'expense_in_wire', wireEffect: -lenderCreditInWire, source: 'computed' });
    expenseInWire += lenderCreditInWire;
  }

  // Manual expenses: always deducted from the warehouse wire.
  for (const item of r.otherLenderCostItems) {
    if (!isManualAdjustmentLineType(item.lineType) || item.amount === 0) {
      continue;
    }
    items.push({ description: item.description, amount: Math.abs(item.amount), bucket: 'expense_in_wire', wireEffect: -item.amount, source: 'line_item' });
    expenseInWire += item.amount;
  }

  // --- 6. Pass-through net ---
  let passThroughNet = 0;

  // Defensive: shortage_recouped reclassified from revenue (items already pushed in step 2)
  if (shortageRecoupedInRevenue !== 0) {
    passThroughNet += shortageRecoupedInRevenue;
  }

  // Shortage (regardless of sign)
  if (r.shortageFunded !== 0) {
    const wireEffect = -r.shortageFunded; // negative shortage → positive wire effect
    items.push({
      description: r.shortageFunded < 0 ? 'Shortage Recoup' : 'Shortage Funded',
      amount: Math.abs(r.shortageFunded),
      bucket: 'pass_through_in_wire',
      wireEffect,
      source: 'computed',
      clearingType: r.shortageFunded < 0 ? 'shortage_recouped' : 'shortage_funded'
    });
    passThroughNet += wireEffect;
  }

  if (r.governmentFundingPassThrough !== 0) {
    items.push({
      description: 'Government Funding Fee Pass-Through',
      amount: Math.abs(r.governmentFundingPassThrough),
      bucket: 'pass_through_in_wire',
      wireEffect: 0,
      source: 'computed',
      clearingType: 'government_funding'
    });
  }

  if (r.buydownPassThrough !== 0) {
    items.push({
      description: 'Temporary Subsidy Buydown Pass-Through',
      amount: Math.abs(r.buydownPassThrough),
      bucket: 'pass_through_in_wire',
      wireEffect: 0,
      source: 'computed',
      clearingType: 'buydown_pass_through'
    });
  }

  if ((r.fhaUfmip?.warehousePremiumAmount || 0) > 0) {
    const warehousePremium = r.fhaUfmip?.warehousePremiumAmount || 0;
    items.push({
      description: 'FHA UFMIP Premium Retained at Closing',
      amount: warehousePremium,
      bucket: 'pass_through_in_wire',
      wireEffect: warehousePremium,
      source: 'computed',
      clearingType: 'fha_ufmip_premium'
    });
    passThroughNet += warehousePremium;
  }

  // Haircut reserve
  const haircutReserve = loanAmount * haircutPercent / 100;
  if (haircutReserve > 0) {
    items.push({
      description: 'Non-QM Haircut Reserve',
      amount: haircutReserve,
      bucket: 'pass_through_in_wire',
      wireEffect: haircutReserve,
      source: 'computed',
      clearingType: 'non_qm_haircut'
    });
    passThroughNet += haircutReserve;
  }

  // Reimbursable fees (in wire, paid to LO separately — $0 P&L)
  if (r.reimbursableItems.some(item => (item.reimbursementAmount ?? item.amount) !== 0)) {
    for (const item of r.reimbursableItems) {
      const reimbursementAmount = item.reimbursementAmount ?? item.amount;
      if (reimbursementAmount !== 0) {
        items.push({
          description: `${item.description} (Reimbursable)`,
          amount: Math.abs(reimbursementAmount),
          bucket: 'pass_through_in_wire',
          wireEffect: reimbursementAmount,
          source: 'line_item',
          clearingType: 'reimbursable'
        });
      }
    }
    passThroughNet += r.totalReimbursable;
  }

  // Manual pass-throughs (received in the wire, remitted to a third party — $0 P&L)
  for (const item of r.manualPassThroughItems) {
    if (item.amount === 0) {
      continue;
    }
    items.push({
      description: `${item.description} (Pass-through)`,
      amount: Math.abs(item.amount),
      bucket: 'pass_through_in_wire',
      wireEffect: item.amount,
      source: 'line_item',
      clearingType: 'manual_pass_through'
    });
    passThroughNet += item.amount;
  }

  // Retained-only non-reimbursable rows (e.g. a conventional MI premium withheld by the warehouse and
  // paid to the MI company by Lendwise): the retained dollars sit in the warehouse basis under the SAME
  // predicate as feesAtClosing, so they are in the wire but never revenue — clear them in-wire, $0 P&L.
  for (const item of r.nonReimbursableUserFeeItems) {
    const retained = item.warehouseRetainedAmount || 0;
    if (retained <= 0 || item.lineType.startsWith(SHORTAGE_PREFIX) || !entersRetainedBasis(item)) {
      continue;
    }
    items.push({
      description: `${item.description} (Retained at closing)`,
      amount: retained,
      bucket: 'pass_through_in_wire',
      wireEffect: retained,
      source: 'line_item',
      clearingType: 'retained_at_closing'
    });
    passThroughNet += retained;
  }

  // Escrow deposit (in fees + in investor costs → typically cancels to $0)
  const escrowNet = escrowDeposit - r.escrowPassThrough;
  if (escrowDeposit > 0 || r.escrowPassThrough > 0) {
    items.push({
      description: 'Escrow Deposit (net)',
      amount: Math.max(escrowDeposit, r.escrowPassThrough),
      bucket: 'pass_through_in_wire',
      wireEffect: escrowNet,
      source: 'computed',
      clearingType: 'escrow'
    });
    passThroughNet += escrowNet;
  }

  // Principal already collected from the borrower reduces the UPB purchased by
  // the investor, so it reduces the residual purchase wire. This is the
  // settlement leg of a balance-sheet clearing flow, not a P&L expense.
  if (servicingPrincipalCollected > 0) {
    items.push({
      description: 'Servicing Principal Settlement',
      amount: servicingPrincipalCollected,
      bucket: 'pass_through_in_wire',
      wireEffect: -servicingPrincipalCollected,
      source: 'computed',
      clearingType: 'servicing_principal'
    });
    passThroughNet -= servicingPrincipalCollected;
  }

  // --- 7. Expense outside wire ---
  let expenseOutside = 0;
  let passThroughOutside = 0;

  if (r.loPayout > 0) {
    items.push({ description: 'LO Compensation', amount: r.loPayout, bucket: 'expense_outside', wireEffect: 0, source: 'computed' });
    expenseOutside += r.loPayout;
  }
  if (r.totalOverrideAmount > 0) {
    items.push({ description: 'Override Compensation', amount: r.totalOverrideAmount, bucket: 'expense_outside', wireEffect: 0, source: 'computed' });
    expenseOutside += r.totalOverrideAmount;
  }
  if (r.volumeBonusAmount > 0) {
    items.push({ description: 'Volume Bonus', amount: r.volumeBonusAmount, bucket: 'expense_outside', wireEffect: 0, source: 'computed' });
    expenseOutside += r.volumeBonusAmount;
  }
  if (excessLC > 0) {
    items.push({ description: 'Excess Lender Credit', amount: excessLC, bucket: 'expense_outside', wireEffect: 0, source: 'computed' });
    expenseOutside += excessLC;
  }
  // Other lender costs — company expenses paid outside the wire (e.g. "Mortgage Insurance Premium
  // paid to MGIC", the expense leg of a warehouse-withheld MI premium).
  // Manual expenses were itemized in the wire in step 5.
  for (const item of r.otherLenderCostItems) {
    if (item.amount === 0 || isManualAdjustmentLineType(item.lineType)) {
      continue;
    }
    items.push({ description: item.description, amount: Math.abs(item.amount), bucket: 'expense_outside', wireEffect: 0, source: 'line_item' });
    expenseOutside += item.amount;
  }

  if ((r.fhaUfmip?.outsideRemittanceAmount || 0) > 0) {
    const remittance = r.fhaUfmip?.outsideRemittanceAmount || 0;
    items.push({
      description: 'FHA UFMIP Remitted to HUD',
      amount: remittance,
      bucket: 'pass_through_outside',
      wireEffect: -remittance,
      source: 'computed',
      clearingType: 'fha_ufmip_hud_remittance'
    });
    passThroughOutside -= remittance;
  }

  if (r.fhaUfmipCompanyVariance > 0) {
    items.push({
      description: 'FHA UFMIP Company Variance',
      amount: r.fhaUfmipCompanyVariance,
      bucket: 'expense_outside',
      wireEffect: 0,
      source: 'computed'
    });
    expenseOutside += r.fhaUfmipCompanyVariance;
  }

  // --- 8. Revenue outside wire ---
  let revenueOutside = 0;

  if (r.loPayout < 0) {
    items.push({ description: 'LO Compensation Deduction Offset', amount: Math.abs(r.loPayout), bucket: 'revenue_outside', wireEffect: 0, source: 'computed' });
    revenueOutside += Math.abs(r.loPayout);
  }

  if (r.servicingInterestIncome > 0) {
    items.push({ description: 'Servicing Interest Income', amount: r.servicingInterestIncome, bucket: 'revenue_outside', wireEffect: 0, source: 'computed' });
    revenueOutside += r.servicingInterestIncome;
  }

  // --- 9. Verify identities ---
  const computedWire = revenueInWire - expenseInWire + passThroughNet;
  const isWireBalanced = Math.abs(wireFromWarehouse - computedWire) <= 0.01;

  const totalRevenue = revenueInWire + revenueOutside;
  const totalExpenses = expenseInWire + expenseOutside;
  const netProfit = totalRevenue - totalExpenses;
  // reconResult.netProfit includes shortageRecoupedInRevenue as revenue;
  // we reclassified it to pass-through, so adjust the expected value.
  const expectedNetProfit = r.netProfit - shortageRecoupedInRevenue;
  const isPnlBalanced = Math.abs(netProfit - expectedNetProfit) <= 0.01;

  if (!isWireBalanced) {
    log.warn('Wire balance identity failed', { wireFromWarehouse, computedWire, drift: wireFromWarehouse - computedWire });
  }
  if (!isPnlBalanced) {
    log.warn('P&L identity failed', { netProfit, expectedNetProfit, drift: netProfit - expectedNetProfit });
  }

  return {
    items,
    revenueInWire,
    expenseInWire,
    passThroughNet,
    passThroughOutside,
    expenseOutside,
    revenueOutside,
    totalRevenue,
    totalExpenses,
    wireFromWarehouse,
    computedWire,
    netProfit,
    isWireBalanced,
    isPnlBalanced,
    basisModeUsed: cashBasis.basisModeUsed,
    cdCreditNettedToWarehouseAdvance: cashBasis.cdCreditNettedToWarehouseAdvance,
    netPayableToLenderAtClosing: cashBasis.netPayableToLenderAtClosing,
    cashNeededAtClosing: cashBasis.cashNeededAtClosing
  };
}
